import React, { useState, useEffect } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Table,
  Spinner,
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { searchServiceItemSKU } from '../ManageTO/actions/action';
import { getModelList } from '../ManageServiceOrders/actions/action';

const initialFormState = {
  title: '',
  sku: '',
  productType: '',
  model: '',
  price: '',
  imgUrl: '',
  isDefaultSelection: 0,
  amcItems: [],
};

const AddServiceItemOffcanvas = ({ isOpen, toggle, onSave, editData }) => {
  const [form, setForm] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchSku, setSearchSku] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [models, setModels] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      const fetchModels = async () => {
        try {
          const response = await getModelList();
          const rawData = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
          if (rawData.length > 0) {
            // Extract unique names from the data
            const uniqueNames = [...new Set(rawData.map(item => item.name))].filter(Boolean);
            setModels(uniqueNames);
          }
        } catch (error) {
          console.error('Error fetching models:', error);
        }
      };
      fetchModels();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchMainSku = async () => {
      // Searching triggered if ProductType is AMC, SKU has enough chars
      if (form.productType === 'AMC' && form.sku.trim().length > 3 && !editData) {
        try {
          const response = await searchServiceItemSKU({ dispatch, params: { sku: form.sku } });
          const items = Array.isArray(response) ? response : (response?.data || response?.result || []);
          if (items && items.length > 0) {
            const item = items[0];
            const newTitle = item.title || '';
            const newPrice = item.price || item.unit_price || item.selling_price || '';
            
            setForm((prev) => {
              const currentFirstAmc = prev.amcItems[0] || {};
              if (currentFirstAmc.title !== newTitle || currentFirstAmc.price !== newPrice) {
                const newAmcItems = [...prev.amcItems];
                if (newAmcItems.length > 0) {
                  newAmcItems[0] = { ...newAmcItems[0], title: newTitle, price: newPrice };
                } else {
                  newAmcItems.push({ title: newTitle, sku: prev.sku, price: newPrice });
                }
                return { ...prev, amcItems: newAmcItems };
              }
              return prev;
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      fetchMainSku();
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [form.sku, form.productType, dispatch, editData]);

  useEffect(() => {
    if (form.productType === 'AMC') {
      const totalPrice = form.amcItems.reduce(
        (acc, item) => acc + (parseFloat(item.price) || 0),
        0
      );
      if (parseFloat(form.price || 0) !== totalPrice) {
        setForm((prev) => ({ ...prev, price: totalPrice }));
      }
    }
  }, [form.amcItems, form.productType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const newState = { ...prev, [name]: value };

      if (newState.productType === 'AMC') {
        if (newState.amcItems.length === 0) {
          newState.amcItems = [{
            title: newState.title,
            sku: newState.sku,
            price: newState.price || ''
          }];
        } else if (name === 'sku') {
          // Sync only SKU to the first item
          const newAmcItems = [...newState.amcItems];
          newAmcItems[0] = {
            ...newAmcItems[0],
            sku: value
          };
          newState.amcItems = newAmcItems;
        }
      }
      return newState;
    });
  };

  const handleAmcItemChange = (index, e) => {
    const { name, value } = e.target;
    const newAmcItems = [...form.amcItems];
    newAmcItems[index] = { ...newAmcItems[index], [name]: value };
    setForm((prev) => ({ ...prev, amcItems: newAmcItems }));
  };

  const removeAmcItem = (index) => {
    const newAmcItems = form.amcItems.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, amcItems: newAmcItems }));
  };

  const handleSearchAndAdd = async () => {
    if (!searchSku.trim()) return;
    setIsSearching(true);
    try {
      const response = await searchServiceItemSKU({ dispatch, params: { sku: searchSku } });
      const items = Array.isArray(response) ? response : (response?.data || response?.result || []);
      if (items && items.length > 0) {
        const item = items[0];
        const itemPrice = item.price || item.unit_price || item.selling_price || '';
        
        const exists = form.amcItems.find((a) => a.sku === searchSku);
        if (exists) {
          alert('Item already added.');
          setSearchSku('');
          return;
        }

        setForm((prev) => ({
          ...prev,
          amcItems: [
            ...prev.amcItems,
            { title: item.title || '', sku: item.sku || searchSku, price: itemPrice },
          ],
        }));
        setSearchSku('');
      } else {
        alert('Item not found against this SKU.');
      }
    } catch (error) {
      console.error('Error searching SKU:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.model || !form.model.trim()) {
      alert('Please select at least one model.');
      return;
    }
    setIsSubmitting(true);
    try {
      const success = await onSave({
        ...form,
        price: Number(form.price),
        id: editData?.id, // Include ID if it's an edit
        isAmc: form.productType === 'AMC' ? 1 : 0,
        isDefaultSelection:
          String(form.productType || '').toLowerCase() === 'service' && form.isDefaultSelection
            ? 1
            : 0,
        amcItems: form.amcItems.map(item => ({
          ...item,
          price: Number(item.price),
          wasPrice: item.wasPrice === '' || item.wasPrice === null || item.wasPrice === undefined ? 0 : Number(item.wasPrice)
        }))
      });
      if (success) {
        toggle();
      }
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setForm({
          title: editData.title || '',
          sku: editData.sku || '',
          productType: (editData.productType || editData.product_type) === 'amc' ? 'AMC' : (editData.productType || editData.product_type) || '',
          model: editData.model || '',
          price: editData.price || '',
          imgUrl: editData.imgUrl || '',
          isDefaultSelection: Number(editData.isDefaultSelection ?? editData.is_default_selection ?? 0) === 1 ? 1 : 0,
          amcItems: (editData.amcItems || []).map(a => ({
            ...a,
            wasPrice: a.wasPrice ?? a.was_price ?? '',
          })),
        });
      } else {
        setForm(initialFormState);
      }
    }
  }, [isOpen, editData]);

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '500px' }}
    >
      <OffcanvasHeader toggle={toggle}>
        {editData ? 'Edit Service Item' : 'Add Service Item'}
      </OffcanvasHeader>
      <OffcanvasBody>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="title">Title</Label>
            <Input
              type="text"
              name="title"
              id="title"
              value={form.title}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="sku">SKU</Label>
                <Input
                  type="text"
                  name="sku"
                  id="sku"
                  value={form.sku}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="productType">Product Type</Label>
                <Input
                  type="select"
                  name="productType"
                  id="productType"
                  value={form.productType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Type</option>
                  <option value="service">service</option>
                  <option value="spare parts">Spare Parts</option>
                  <option value="consumables">Consumables</option>
                  <option value="AMC">AMC</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="model">Model</Label>
                <Select
                  inputId="model"
                  isMulti
                  options={[
                    { value: 'All', label: 'All' },
                    ...models.map((modelName) => ({ value: modelName, label: modelName })),
                  ]}
                  value={(form.model ? form.model.split(',') : [])
                    .map((m) => m.trim())
                    .filter(Boolean)
                    .map((m) => ({ value: m, label: m }))}
                  onChange={(selected) =>
                    setForm((prev) => ({
                      ...prev,
                      model: (selected || []).map((opt) => opt.value).join(','),
                    }))
                  }
                  placeholder="Select model no"
                  closeMenuOnSelect={false}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="price">Unit Price</Label>
                <Input
                  type="number"
                  name="price"
                  id="price"
                  value={form.price}
                  onChange={handleChange}
                  required
                  disabled={form.productType === 'AMC'}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup className="mb-3">
            <Label for="imgUrl">Image URL</Label>
            <Input
              type="text"
              name="imgUrl"
              id="imgUrl"
              value={form.imgUrl}
              onChange={handleChange}
            />
          </FormGroup>
          {String(form.productType || '').toLowerCase() === 'service' && (
            <FormGroup className="mb-4 form-check form-switch">
              <Input
                type="checkbox"
                role="switch"
                className="form-check-input"
                name="isDefaultSelection"
                id="isDefaultSelection"
                checked={!!form.isDefaultSelection}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    isDefaultSelection: e.target.checked ? 1 : 0,
                  }))
                }
              />
              <Label for="isDefaultSelection" check className="ms-2">
                Default Selection
              </Label>
            </FormGroup>
          )}

          {form.productType === 'AMC' && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">AMC Items</h6>
                <div className="d-flex gap-2">
                  <Input
                    type="text"
                    bsSize="sm"
                    placeholder="Search SKU"
                    value={searchSku}
                    onChange={(e) => setSearchSku(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearchAndAdd();
                      }
                    }}
                    style={{ width: '150px' }}
                  />
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleSearchAndAdd}
                    type="button"
                    disabled={isSearching || !searchSku.trim()}
                  >
                    {isSearching ? <Spinner size="sm" /> : <i className="fa fa-search me-1"></i>} Search & Add
                  </Button>
                  {/* <Button color="success" size="sm" onClick={addAmcItem} type="button">
                    <i className="fa fa-plus me-1"></i> Add AMC Item
                  </Button> */}
                </div>
              </div>
              <Table size="sm" bordered>
                <thead>
                  <tr className="bg-light">
                    <th>Title</th>
                    <th>SKU</th>
                    <th>Was Price</th>
                    <th>Price</th>
                    <th style={{ width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.amcItems.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <Input
                          type="text"
                          name="title"
                          value={item.title}
                          onChange={(e) => handleAmcItemChange(index, e)}
                          required
                          size="sm"
                          disabled
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          name="sku"
                          value={item.sku}
                          onChange={(e) => handleAmcItemChange(index, e)}
                          required
                          size="sm"
                          disabled
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          name="wasPrice"
                          value={item.wasPrice ?? ''}
                          onChange={(e) => handleAmcItemChange(index, e)}
                          size="sm"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          name="price"
                          value={item.price}
                          onChange={(e) => handleAmcItemChange(index, e)}
                          required
                          size="sm"
                        />
                      </td>
                      <td className="text-center p-1">
                        {index !== 0 && (
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => removeAmcItem(index)}
                            type="button"
                            className="btn-icon"
                          >
                            <i className="fa fa-trash"></i>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {form.amcItems.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        No AMC items added
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <div className="mt-5 d-flex gap-2">
            <Button color="secondary" type="submit" className="flex-grow-1 py-2" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {editData ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                editData ? 'Update Service Item' : 'Save Service Item'
              )}
            </Button>
            <Button color="light" type="button" onClick={toggle} className="px-4" disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default AddServiceItemOffcanvas;
